/*
 * Maps a failed API call (an axios error) to a friendly, translated
 * message that the frontend derives from the HTTP status alone. The
 * backend's own message text is deliberately not used here: its texts
 * are only partly translated and sometimes raw exception or database
 * strings (see requirements/backend_requirements.md, BE-9), so it
 * belongs into the collapsible technical details of the ErrorDialog,
 * not into the headline. `fallbackKey` lets the caller pick a
 * context-appropriate default (e.g. loading vs. saving).
 */
export function apiErrorMessage(
  error,
  t,
  { fallbackKey = 'error.generic' } = {}
) {
  /* no response object means the request never completed: network
     down, timeout, CORS, server unreachable */
  if (error?.response == null) {
    return t('error.network');
  }

  switch (error.response.status) {
    case 401:
    case 403:
      return t('error.forbidden');
    case 404:
      return t('error.notFound');
    case 409:
      return isForeignKeyViolation(error.response.data)
        ? t('error.stillInUse')
        : t('error.conflict');
    default:
      return t(fallbackKey);
  }
}

/*
 * A refused delete reaches the client as a 409 carrying the database's
 * own words (BE-2). Those words distinguish it from a genuine edit
 * conflict: a foreign key violation means the record is still
 * referenced by other data, not that it changed in the meantime.
 */
function isForeignKeyViolation(data) {
  const raw =
    typeof data === 'string'
      ? data
      : typeof data?.message === 'string'
        ? data.message
        : '';
  return raw.toLowerCase().includes('violates foreign key constraint');
}

/*
 * The technical side of the same error, for the collapsible details of
 * the ErrorDialog: the HTTP status, the backend's own message, the
 * error id the backend will send once BE-10 is built, and the
 * transport-level message (e.g. "Network Error") for calls that never
 * got a response.
 */
export function apiErrorDetails(error) {
  const data = error?.response?.data;
  return {
    status: error?.response?.status ?? null,
    backendMessage: serverErrorMessage(data),
    errorId:
      typeof data === 'object' && typeof data?.errorId === 'string'
        ? data.errorId
        : null,
    transportMessage: typeof error?.message === 'string' ? error.message : null,
  };
}

/*
 * The backend (CustomErrorResponse) wraps its error text in a `message`
 * field, prefixed with "ERROR: [<status>] " and — when a translation is
 * missing — decorated with 【⍰ … ⍰】. Unwrap both and return the plain
 * message; the status has its own line in the details. Returns null for
 * empty or non-human bodies (e.g. a plain string HTML error page).
 */
function serverErrorMessage(data) {
  const raw =
    typeof data === 'string'
      ? data
      : typeof data?.message === 'string'
        ? data.message
        : null;
  if (!raw) {
    return null;
  }

  const cleaned = raw
    .replace(/^ERROR:\s*\[\d+\]\s*/i, '')
    .replace(/^【⍰\s*/, '')
    .replace(/\s*⍰】$/, '')
    .trim();

  /* ignore empty payloads or an HTML error page served as a string */
  if (!cleaned || cleaned.startsWith('<')) {
    return null;
  }
  return cleaned;
}
